import {
  InfluencerNewProfileHeaderV2,
  SocialAccountStatus,
  SocialAccountType,
  TTCMStatus,
  useInfluencerProfileEstimatedAudienceQuery,
  useTiktokProfileAudienceQuery
} from '@anytag/graphql';
import { ChartsContainer } from '@anytag/shared-atoms';
import { HAS_NO_ESTIMATED_TEXT } from '@anytag/shared-constants';
import {
  AgeBreakdownChart,
  AgeBreakdownData,
  AreaBreakdownChart,
  AreaBreakdownData,
  GenderBreakdownChart,
  GenderBreakdownData
} from '@anytag/shared-molecules';
import { useInfluencerProfileOpacityContent } from '../../hooks';
import { InfluencerSelectedAccountInfo } from '../../types';
import { InfluencerProfileAudienceWrapper } from '../shared';

export interface InfluencerTiktokProfileAudienceProps {
  socialAccount: InfluencerSelectedAccountInfo;
  influencerProfile: InfluencerNewProfileHeaderV2;
}

export const InfluencerTiktokProfileAudience = ({
  socialAccount,
  influencerProfile: { id: influencerId, status }
}: InfluencerTiktokProfileAudienceProps) => {
  const isScraping = status === SocialAccountStatus.SCRAPING;
  const { opacityContent, hasEstimatedDemographics } = useInfluencerProfileOpacityContent({
    influencerId,
    socialAccount,
    skipEstimateContent: isScraping // Scraping account always use estimated data so no need to check
  });
  const { data, loading } = useTiktokProfileAudienceQuery({
    skip: isScraping,
    variables: { pk: influencerId, socialAccountId: socialAccount.id }
  });
  const { data: estimatedAudience, loading: estimatedAudienceLoading } = useInfluencerProfileEstimatedAudienceQuery({
    variables: {
      socialAccountId: socialAccount.id,
      socialAccountType: socialAccount.type as SocialAccountType
    }
  });

  const audienceData = data?.tiktokProfileAudience;
  const estimatedAudienceData = estimatedAudience?.influencerProfileEstimatedAudience;
  const isTTCMCreator = !!socialAccount.ttcmStatus && socialAccount.ttcmStatus === TTCMStatus.APPROVED;
  const chartOpacityContent =
    !isScraping && hasEstimatedDemographics ? opacityContent : { wrapperText: HAS_NO_ESTIMATED_TEXT };
  const formattedAudienceData = {
    genderData: {
      maleRate: isScraping ? estimatedAudienceData?.gender?.maleRate : audienceData?.maleRate,
      femaleRate: isScraping ? estimatedAudienceData?.gender?.femaleRate : audienceData?.femaleRate
    } satisfies GenderBreakdownData,
    ageData: isScraping
      ? estimatedAudienceData?.ageGroup?.groups?.reduce<AgeBreakdownData>((acc, curr) => {
          if (curr) {
            acc.maleRate = [...(acc.maleRate || []), curr.maleRate];
            acc.femaleRate = [...(acc.femaleRate || []), curr.femaleRate];
            acc.ageGroup = [...(acc.ageGroup || []), { start: curr.group.at(0), end: curr.group.at(1) }];
          }

          return acc;
        }, {})
      : ({
          ageGroup: audienceData?.ageRates.ageGroup,
          totalRate: audienceData?.ageRates.rate
        } satisfies AgeBreakdownData),
    areaData: {
      followerRegionRate:
        !isScraping && isTTCMCreator
          ? undefined
          : estimatedAudienceData?.location?.regions?.map((region) => ({
              name: region.regionName,
              rate: region.rate
            })),
      followerCountryRate:
        !isScraping && isTTCMCreator
          ? audienceData?.countryRates?.map((item) => ({ id: item.countryId, rate: item.rate }))
          : estimatedAudienceData?.location?.countries?.reduce<Array<{ id: string; rate: number }>>(
              (acc, country) => (country ? [...acc, { id: country.countryId, rate: country.rate }] : acc),
              []
            )
    } satisfies AreaBreakdownData
  };

  return (
    <InfluencerProfileAudienceWrapper
      data={formattedAudienceData}
      loading={loading || estimatedAudienceLoading}
      hasEstimatedDemographics={isScraping || hasEstimatedDemographics}
    >
      {({ data: { genderData, ageData, areaData } }) => (
        <ChartsContainer
          css={{ height: '385px' }}
          elements={[
            <GenderBreakdownChart
              {...genderData}
              {...chartOpacityContent}
              hasEstimatedDemographics={isScraping || chartOpacityContent.hasEstimatedDemographics}
            />,
            <AgeBreakdownChart
              {...ageData}
              {...chartOpacityContent}
              hasEstimatedDemographics={isScraping || chartOpacityContent.hasEstimatedDemographics}
            />,
            <AreaBreakdownChart
              {...areaData}
              hasEstimatedDemographics={isScraping || hasEstimatedDemographics || !isTTCMCreator}
              hasCountriesRates
            />
          ]}
        />
      )}
    </InfluencerProfileAudienceWrapper>
  );
};